import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
//import './CustomerReturnsList.css';
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
    Spinner
} from 'reactstrap';
import {
    FaCheckCircle,
    FaEdit,
    FaFilePdf,
    FaFilter,
    FaMoneyBillWave,
    FaPlus,
    FaSearch,
    FaTrash,
    FaUndo
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { customerReturnActions } from '../../sagas/customerReturnSlice';
import { formatPrice } from '../../helpers/utility';
import CustomerReturnForm from './CustomerReturnForm';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from "react-select";
import { clientActions } from '../../sagas/clientSlice';
import { productActions } from '../../sagas/productSlice';
import logo from "../../assets/images/logo.png";

const loadImageBase64 = (src) =>
    new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null); // fail-safe
        img.src = src;
    });


const CustomerReturnsList = () => {
    const dispatch = useDispatch();
    const { customerReturns, loading } = useSelector((state) => state.customerReturns);
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();
    const { company } = useSelector((s) => s.auth);

    const [selectedReturn, setSelectedReturn] = useState(null);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const searchInputRef = useRef(null);
    const clients = useSelector(state => state.clients.clients);
    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unites

    // Filter states
    const [filters, setFilters] = useState({
        clientId: '',
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(tomorrowDate),
        query: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [customerReturns, filters]);

    useEffect(() => {
        if (clients && clients.length == 0) {
            dispatch(clientActions.search({ query: "" }));
        }
        dispatch(productActions.findAllUnites());
    }, []);

    useEffect(() => {
        dispatch(customerReturnActions.list({
            ...filters,
        }));
    }, [filters.clientId, filters.dateFrom, filters.dateTo]);


    const debouncedSearch = () => {
        dispatch(customerReturnActions.list({
            ...filters,
        }));
    }

    const clearFilters = () => {
        setFilters({
            query: '',
            clientId: null,
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(tomorrowDate)
        });
        debouncedSearch();
    };

    const clientOptions = clients?.map(client => ({
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers || []
    })) ?? [];

    const clientOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...clientOptions
    ];


    const handleShowModal = () => {
        setShowModal(true);
    };

    const handleEdit = (returnItem) => {
        setSelectedReturn(returnItem);
        setShowModal(true);
    }

    const handleCloseModal = () => {
        setShowModal(false);
        if (selectedReturn) {
            setSelectedReturn(null);
        }

        const payload = {
            ...filters
        };
        dispatch(customerReturnActions.list(payload));
    };

    const fmt = (n) => {
        const x = Number(n || 0);
        const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
        return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    const safe = (v, fb = '-') => (v || v === 0 ? v : fb);
    const toDateFR = (d) => {
        try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return '-'; }
    };

    const generateReturnReceipt = async (returnItem, t = (k) => k, opts = {}) => {

        const {
            devise = 'DH',
            open = true,
            fileName = `BON_RETOUR_${safe(returnItem?.numeroRetour, returnItem?.id)}.pdf`,
        } = opts;

        const logoBase64 = await loadImageBase64(logo);
        const normalizePdfText = (value, fallback = '-') => {
            if (value === null || value === undefined || value === '') {
                return fallback;
            }

            return String(value)
                .normalize('NFKC')
                .replace(/\s+/g, ' ')
                .trim();
        };

        const rawLines =
            returnItem?.lignes ||
            returnItem?.lignesRetour ||
            returnItem?.lines ||
            [];

        const rows = rawLines.map((ln) => {
            const code = normalizePdfText(ln.articleCode, '-');
            const name = normalizePdfText(ln.articleNom, '-');
            const unite = normalizePdfText(unites.find(u => u.code === ln.unite)?.libelle || ln.unite, 'U');
            const qte = Number(ln.quantiteRetournee || 0);
            const tva = Number(ln.tauxTVA ?? 0);
            const puTTC = Number(ln.prixUnitaireTTC || 0);
            const totalTTC = Number(ln.montantTTC || (qte * puTTC));
            return { code, name, unite, qte, tva, puTTC, totalTTC };
        });

        const totalTTC = Number(returnItem?.montantTTC || 0);
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const primary = [6, 145, 43];
        const ink = [15, 23, 42];
        const subtle = [100, 116, 139];
        const line = [226, 232, 240];
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 40;
        const topY = 34;
        const contentWidth = pageWidth - (marginX * 2);
        const cardGap = 16;
        const cardWidth = (contentWidth - cardGap) / 2;
        const cardPaddingX = 14;
        const cardPaddingY = 14;
        const lineHeight = 13;
        const headerRightX = pageWidth - marginX;
        const splitLine = (text, width) => doc.splitTextToSize(normalizePdfText(text, ''), width);

        const drawInfoCard = (x, y, title, lines) => {
            const innerWidth = cardWidth - (cardPaddingX * 2);
            const wrappedTitle = splitLine(title, innerWidth);
            const wrappedLines = lines
                .filter(Boolean)
                .map((item) => splitLine(item, innerWidth))
                .filter((item) => item.length > 0);

            const contentHeight =
                (wrappedTitle.length * lineHeight) +
                10 +
                wrappedLines.reduce((sum, item) => sum + (item.length * lineHeight), 0);

            const cardHeight = Math.max(82, contentHeight + (cardPaddingY * 2));

            doc.setDrawColor(...line);
            doc.setFillColor(248, 250, 252);
            doc.roundedRect(x, y, cardWidth, cardHeight, 8, 8, 'FD');

            let cursorY = y + cardPaddingY + 10;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(...ink);
            doc.text(wrappedTitle, x + cardPaddingX, cursorY);
            cursorY += (wrappedTitle.length * lineHeight) + 5;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(...subtle);

            wrappedLines.forEach((wrapped) => {
                doc.text(wrapped, x + cardPaddingX, cursorY);
                cursorY += wrapped.length * lineHeight;
            });

            return cardHeight;
        };

        if (logoBase64) {
            doc.addImage(logoBase64, 'PNG', marginX, topY, 96, 54);
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(19);
        doc.setTextColor(...primary);
        doc.text('BON DE RETOUR', headerRightX, topY + 12, { align: 'right' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...subtle);
        doc.text(`# ${normalizePdfText(returnItem?.numeroRetour, returnItem?.id)}`, headerRightX, topY + 30, { align: 'right' });
        doc.text(toDateFR(returnItem?.dateOperation || new Date()), headerRightX, topY + 44, { align: 'right' });

        doc.setDrawColor(...line);
        doc.line(marginX, topY + 70, pageWidth - marginX, topY + 70);

        const companyLines = [
            normalizePdfText(company?.name, 'Entreprise'),
            normalizePdfText(company?.address, ''),
            [company?.phone1, company?.phone2].filter(Boolean).length
                ? `Tel : ${[company.phone1, company.phone2].filter(Boolean).join(' / ')}`
                : '',
            normalizePdfText(company?.email, ''),
        ].filter(Boolean);

        const modeMap = {
            AVOIR: 'Avoir',
            REMBOURSEMENT: 'Remboursement',
            ECHANGE: 'Echange',
        };
        const statutMap = {
            BROUILLON: 'Brouillon',
            VALIDE: 'Valide',
            ANNULE: 'Annule',
            COMPTABILISE: 'Comptabilise',
        };

        const clientLines = [
            normalizePdfText(returnItem?.clientNom, '-'),
            returnItem?.commercialNom ? `Commercial : ${normalizePdfText(returnItem.commercialNom, '-')}` : '',
            returnItem?.numBL ? `BL : ${normalizePdfText(returnItem.numBL, '-')}` : '',
            `Mode : ${modeMap[returnItem?.modeRemboursement] || '-'}`,
            `Statut : ${statutMap[returnItem?.statut] || '-'}`,
        ].filter(Boolean);

        const cardsY = topY + 88;
        const leftCardHeight = drawInfoCard(marginX, cardsY, 'Entreprise', companyLines);
        const rightCardHeight = drawInfoCard(marginX + cardWidth + cardGap, cardsY, 'Client', clientLines);
        const tableStartY = cardsY + Math.max(leftCardHeight, rightCardHeight) + 22;

        autoTable(doc, {
            startY: tableStartY,
            head: [[
                'Code',
                'Designation',
                'Qte',
                'Unite',
                'PU TTC',
                'TVA',
                'Total TTC',
            ]],
            body: rows.map(r => [
                r.code,
                r.name,
                fmt(r.qte),
                r.unite,
                `${fmt(r.puTTC)} ${devise}`,
                `${Math.round(r.tva * 100)}%`,
                `${fmt(r.totalTTC)} ${devise}`,
            ]),
            theme: 'grid',
            styles: {
                fontSize: 9,
                cellPadding: { top: 7, right: 6, bottom: 7, left: 6 },
                textColor: ink,
                lineColor: line,
                lineWidth: 0.5,
                overflow: 'linebreak',
                valign: 'middle',
            },
            headStyles: {
                fillColor: primary,
                textColor: 255,
                fontStyle: 'bold',
                halign: 'center',
            },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: {
                0: { cellWidth: 62, halign: 'center' },
                1: { cellWidth: 180, halign: 'left' },
                2: { cellWidth: 48, halign: 'center' },
                3: { cellWidth: 55, halign: 'center' },
                4: { cellWidth: 72, halign: 'right' },
                5: { cellWidth: 48, halign: 'center' },
                6: { cellWidth: 76, halign: 'right' },
            },
            margin: { left: marginX, right: marginX, bottom: 64 },
            didParseCell: (data) => {
                if (data.section === 'body' && data.column.index === 1) {
                    data.cell.styles.fontStyle = 'normal';
                    data.cell.styles.cellPadding = { top: 8, right: 6, bottom: 8, left: 8 };
                }
            },
        });

        const endY = doc.lastAutoTable.finalY || tableStartY;
        const totalBoxWidth = 170;
        const totalBoxHeight = 34;
        const totalBoxX = pageWidth - marginX - totalBoxWidth;
        const totalBoxY = endY + 18;

        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(...line);
        doc.roundedRect(totalBoxX, totalBoxY, totalBoxWidth, totalBoxHeight, 8, 8, 'FD');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...ink);
        doc.text('Total TTC', totalBoxX + 14, totalBoxY + 22);
        doc.text(`${fmt(totalTTC)} ${devise}`, totalBoxX + totalBoxWidth - 14, totalBoxY + 22, { align: 'right' });

        const footerY = pageHeight - 24;
        doc.setDrawColor(...line);
        doc.line(marginX, footerY - 14, pageWidth - marginX, footerY - 14);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...subtle);
        doc.text(normalizePdfText(company?.name, 'Entreprise'), marginX, footerY);
        doc.text(`Document genere le ${toDateFR(new Date())}`, pageWidth - marginX, footerY, { align: 'right' });

        if (open) {
            const blobUrl = doc.output('bloburl');
            window.open(blobUrl, '_blank');
        } else {
            doc.save(fileName);
        }
    };


    const handleValidateReturn = (returnItem) => {
        Swal.fire({
            title: t('customerReturns.confirmValidate'),
            text: t('customerReturns.confirmValidateMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('customerReturns.validatedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(customerReturnActions.list(refreshPayload));
                },
            };
            dispatch(customerReturnActions.validate(payload));
        });
    }

    const handleResetReturn = (returnItem) => {
        Swal.fire({
            title: t('customerReturns.resetConfirmTitle', 'Réinitialiser le retour'),
            text: t(
                'customerReturns.resetConfirmText',
                'Cette action va remettre ce retour à l’état brouillon. Voulez-vous continuer ?'
            ),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            const payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('customerReturns.resetSuccess', 'Le retour client a été réinitialisé avec succès.'),
                    });
                    dispatch(customerReturnActions.list({
                        ...filters
                    }));
                },
                onError: () => {
                    Swal.fire({
                        icon: 'error',
                        title: t('common.error'),
                        text: t('customerReturns.resetError', 'Erreur lors de la réinitialisation du retour client.'),
                    });
                }
            };
            dispatch(customerReturnActions.reset(payload));
        });
    }

    const handleDeleteReturn = (returnItem) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('customerReturns.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.deleted'),
                        text: t('customerReturn.deletedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(customerReturnActions.list(refreshPayload));
                },
            };
            dispatch(customerReturnActions.delete(payload));
        });
    }

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUndo className="text-primary me-2" />
                                {t('customerReturns.list')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="primary" onClick={handleShowModal}>
                                    <FaPlus className="me-2" /> {t('customerReturns.new')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <Row className="mb-3 g-2 align-items-end">
                                    <Col lg={4} md={4} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('reglement.search')}</Label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <FaSearch />
                                                </span>
                                                <Input
                                                    type="text"
                                                    innerRef={searchInputRef}
                                                    placeholder={t('customerReturns.searchPlaceholder')}
                                                    value={filters.query}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            query: value
                                                        }));
                                                    }}
                                                    className="text-uppercase"
                                                    style={{ textTransform: 'uppercase' }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            debouncedSearch();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup className="mb-0">
                                            <Label htmlFor="clientId">{t('bonLivraison.client')}:</Label>
                                            <Select
                                                id="clientId"
                                                options={clientOptionsWithAll}
                                                value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                                                onChange={(selectedOption) => {
                                                    setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                                                }}
                                                placeholder={t('bonLivraison.selectClient')}
                                                isSearchable
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateFrom" className="mb-1">Date début</Label>
                                            <Input
                                                type="date"
                                                id="dateFrom"
                                                value={filters.dateFrom}
                                                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateTo" className="mb-1">Date fin</Label>
                                            <Input
                                                type="date"
                                                id="dateTo"
                                                value={filters.dateTo}
                                                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={clearFilters}
                                            title={t('common.clearFilters')}
                                            className="mb-3"
                                            style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FaFilter />
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover table-striped table-bordered">
                                    <thead className="thead-light">
                                        <tr>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.numero')}
                                            </th>
                                            <th className="align-middle text-center">
                                                Num BL
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.date')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('delivery.commercial')}
                                            </th>
                                            
                                            <th className="align-middle text-center">
                                                {t('customerReturns.client')}
                                            </th>
                                            
                                            <th className="align-middle text-center">
                                                {t('customerReturns.montantTTC')}
                                            </th>
                                            
                                            <th className="align-middle text-center">
                                                {t('customerReturns.status')}
                                            </th>
                                            <th className="align-middle text-center">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customerReturns.map((returnItem) => (
                                            <tr key={returnItem.id}>
                                                <td className="align-middle text-center">
                                                    {returnItem.numeroRetour}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {returnItem.numBL || '-'}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {new Date(returnItem.dateOperation).toLocaleDateString('fr-FR')}
                                                </td>

                                                <td className="align-middle text-center">
                                                    {returnItem.commercialNom || 'N/A'}
                                                </td>
                                               
                                                <td className="align-middle text-center">
                                                    {returnItem.clientNom || 'N/A'}
                                                </td>
                                                
                                                <td className="align-middle text-center">{formatPrice(returnItem.montantTTC, false)}</td>
                                                <td className="align-middle text-center">
                                                    {returnItem.statut === 'BROUILLON' && (
                                                        <span className="badge bg-warning">{t('customerReturns.brouillon')}</span>
                                                    )}
                                                    {returnItem.statut === 'ANNULE' && (
                                                        <span className="badge bg-danger">{t('customerReturns.annule')}</span>
                                                    )}
                                                    {returnItem.statut === 'VALIDE' && (
                                                        <span className="badge bg-success">{t('customerReturns.valide')}</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    <div className="btn-group">
                                                        <Button
                                                            color="secondary"
                                                            title={t('customerReturns.generatePdf')}
                                                            onClick={() => generateReturnReceipt(returnItem)}
                                                            className="me-1"
                                                            size="sm"
                                                        >
                                                            <FaFilePdf />
                                                        </Button>
                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="success"
                                                                title={t('customerReturns.validate')}
                                                                onClick={() => handleValidateReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaCheckCircle />
                                                            </Button>
                                                        )}
                                                        {returnItem.statut === 'VALIDE' && (
                                                            <Button
                                                                color="danger"
                                                                title={t('customerReturns.reset', 'Réinitialiser')}
                                                                onClick={() => handleResetReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaUndo />
                                                            </Button>
                                                        )}

                                                        <Button
                                                            color="warning"
                                                            title={t('customerReturns.edit')}
                                                            onClick={() => handleEdit(returnItem)}
                                                            size="sm"
                                                            className="me-1"
                                                        >
                                                            <FaEdit />
                                                        </Button>

                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="danger"
                                                                title={t('customerReturns.delete')}
                                                                onClick={() => handleDeleteReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {
                                            customerReturns.length === 0 && (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {
                                                                t('customerReturns.noReturns')
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light fw-bold">
                                            <td colSpan="5" className="text-end">{t('common.totals')}</td>
                                            <td className="text-center">{formatPrice(customerReturns.reduce((sum, ret) => sum + (ret.montantTTC || 0), 0), false)}</td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                        </>
                    )}
                </CardBody>
            </Card>

            {/* Modal for Customer Return Form */}
            <Modal isOpen={showModal} toggle={handleCloseModal} size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseModal}>
                    {selectedReturn ? `${t('customerReturns.edit')} N° ${selectedReturn.numeroRetour}` : t('customerReturns.new')}
                </ModalHeader>
                <ModalBody>
                    <CustomerReturnForm customerReturn={selectedReturn} onClose={handleCloseModal} />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default CustomerReturnsList;

